import React from 'react';
import { Building2, Edit, UserX, UserCheck, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { Supplier } from '@/types/suppliers.types';

interface SuppliersTableProps {
  suppliers: Supplier[];
  loading: boolean;
  onEdit: (supplier: Supplier) => void;
  onToggleStatus: (supplier: Supplier) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string) => string;
}

type SortField = 'businessName' | 'contact' | 'region' | 'lastOrderDate' | 'isActive';

export const SuppliersTable: React.FC<SuppliersTableProps> = ({
  suppliers,
  loading,
  onEdit,
  onToggleStatus,
  formatCurrency,
  formatDate
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    businessName: 'Proveedor',
    contact: 'Contacto',
    region: 'Ubicación',
    lastOrderDate: 'Último Pedido',
    isActive: 'Estado'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (supplier: Supplier, field: SortField): any => {
    switch (field) {
      case 'businessName':
        return supplier.businessName.toLowerCase();
      case 'contact':
        return supplier.primaryContact.name.toLowerCase();
      case 'region':
        return supplier.address.region.toLowerCase();
      case 'lastOrderDate':
        return supplier.lastOrderDate ? new Date(supplier.lastOrderDate) : new Date(0);
      case 'isActive':
        return supplier.isActive ? 1 : 0;
      default:
        return supplier.businessName;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Supplier, SortField>(
    {
      data: suppliers,
      defaultSortField: 'businessName',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );
  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-700'
      : 'bg-gray-100 text-gray-700';
  };

  const getRegionColor = (region: string) => {
    const colors = {
      'Lima': 'bg-blue-50 text-blue-600',
      'Arequipa': 'bg-orange-50 text-orange-600',
      'Trujillo': 'bg-green-50 text-green-600',
      'Cusco': 'bg-purple-50 text-purple-600',
      'Piura': 'bg-yellow-50 text-yellow-600',
      'Chiclayo': 'bg-pink-50 text-pink-600'
    };
    return colors[region as keyof typeof colors] || 'bg-gray-50 text-gray-600';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-macartur-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando proveedores...</p>
      </div>
    );
  }

  if (suppliers.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay proveedores</h3>
        <p className="text-gray-500">No se encontraron proveedores con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
        <thead className="bg-gray-50 border-b">
          <tr>
            <SortableHeader
              field="businessName"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-left"
            >
              Proveedor
            </SortableHeader>
            <SortableHeader
              field="contact"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-left"
            >
              Contacto
            </SortableHeader>
            <SortableHeader
              field="region"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Ubicación
            </SortableHeader>
            <SortableHeader
              field="lastOrderDate"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Último Pedido
            </SortableHeader>
            <SortableHeader
              field="isActive"
              onSort={tableData.handleSort}
              getSortIcon={tableData.getSortIcon}
              className="text-center"
            >
              Estado
            </SortableHeader>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tableData.pagination.paginatedData.map((supplier) => {
            return (
              <tr key={supplier.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {supplier.businessName}
                    </div>
                    <div className="text-xs text-gray-400">
                      RUC: {supplier.ruc}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">
                      {supplier.primaryContact.name}
                    </div>
                    {supplier.primaryContact.position && (
                      <div className="text-xs text-gray-500">
                        {supplier.primaryContact.position}
                      </div>
                    )}
                    {supplier.primaryContact.phone && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Phone className="h-3 w-3" />
                        {supplier.primaryContact.phone}
                      </div>
                    )}
                  </div>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <div className="text-sm">
                    <Badge 
                      variant="outline" 
                      className={`${getRegionColor(supplier.address.region)} border-0 mb-1`}
                    >
                      {supplier.address.region}
                    </Badge>
                    <div className="flex items-center gap-1 justify-center text-xs text-gray-500">
                      <MapPin className="h-3 w-3" />
                      {supplier.address.city}
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {supplier.lastOrderDate ? formatDate(supplier.lastOrderDate) : 'N/A'}
                  </div>
                  {supplier.totalOrders && (
                    <div className="text-xs text-gray-500">
                      {supplier.totalOrders} pedidos
                    </div>
                  )}
                </td>
                
                
                <td className="px-4 py-4 text-center">
                  <Badge className={getStatusColor(supplier.isActive)}>
                    {supplier.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </td>
                
                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(supplier)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onToggleStatus(supplier)}
                      className={`text-xs ${
                        supplier.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                    >
                      {supplier.isActive ? (
                        <UserX className="w-3 h-3 mr-1" />
                      ) : (
                        <UserCheck className="w-3 h-3 mr-1" />
                      )}
                      {supplier.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};